import type { CreateContactBody, ListContactsQuery, UpdateContactBody } from '@crm/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/api/client';
import { useToast } from '@/components/ui/toast';
import { queryKeys } from '@/lib/query-keys';

export function useContacts(params: ListContactsQuery) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.contacts.list(params),
    queryFn: () => api.contacts.list(params),
    /**
     * Without this the table unmounts to a skeleton on every page change and
     * the page visibly jumps. keepPreviousData holds the old rows in place and
     * flags `isPlaceholderData`, which the UI uses to dim rather than blank.
     */
    placeholderData: keepPreviousData,
  });

  // Warm the next page while the operator reads this one. Costs one request
  // that would have been made anyway, and makes Next feel instant.
  const { limit = 25, offset = 0 } = params;
  const total = query.data?.total ?? 0;
  const hasNext = offset + limit < total;

  useEffect(() => {
    if (!hasNext || query.isPlaceholderData) return;
    const next = { ...params, offset: offset + limit };
    void qc.prefetchQuery({
      queryKey: queryKeys.contacts.list(next),
      queryFn: () => api.contacts.list(next),
    });
  }, [qc, params, offset, limit, hasNext, query.isPlaceholderData]);

  return query;
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id ?? ''),
    queryFn: () => api.contacts.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactBody) => api.contacts.create(body),
    onSuccess: (created) => {
      toast.success('Contact created');
      // Seed the detail cache so opening the new record is instant and does not
      // refetch what the server just returned.
      qc.setQueryData(queryKeys.contacts.detail(created.id), created);
      // Lists are now stale — totals and ordering both changed. Details are not.
      void qc.invalidateQueries({ queryKey: queryKeys.contacts.lists() });
    },
  });
}

/**
 * Field edit. The server returns the saved row, so the detail cache is seeded
 * from the response rather than refetched — and the lists go stale because a
 * name, status or flag change reorders and re-filters them.
 */
export function useUpdateContact(id: string) {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateContactBody) => api.contacts.update(id, body),
    onSuccess: (contact) => {
      toast.success('Contact saved');
      qc.setQueryData(queryKeys.contacts.detail(contact.id), contact);
      void qc.invalidateQueries({ queryKey: queryKeys.contacts.lists() });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}
